// Quote Builder — SJB cabinet catalogue (2026 DTN price file).
//
// Owns the three quote-level defaults (board colour, hinge finish, drawer
// system), the per-line overrides, and the mapping from a priced API response
// onto a QuoteItem.
//
// ── Why this is a new module and not an extension of cabinet-picker.ts ─────
// The W8 picker prices the 2024 DTN catalogue, where the finish only SELECTS
// a pre-computed column (pg1..pg5). The 2026 file ships one carcass list
// price plus a per-colour adjustment, and that adjustment applies to the
// carcass only — hinges and drawer boxes are always at full list. Bolting
// that onto the W8 picker would mean two pricing models in one file with a
// supplier flag deciding which is live; the 2024 catalogue is still in use,
// so it keeps its own path untouched.
//
// The reusable part is the CONVENTION: a line still carries
// {listPrice, discPct} and the existing calcTrade chain still derives the
// trade price. pricing.ts:lineTrade adds `sjbHardwareTotal` on top, which is
// undefined (and therefore 0) for every other supplier.
//
// ── The default/override contract ──────────────────────────────────────────
// setSjbDefaults() writes ONLY to state.defaults.sjb. It deliberately does
// not iterate zones or items: a line's colour is owned by the line from the
// moment it is added, so changing the quote default afterwards affects new
// lines only. This mirrors the `markupPct` precedent (types.ts) and is
// covered by a regression test in sjb-catalogue.test.ts.

import { api } from "@tkos/api-client";
import type {
  QuoteItem,
  SjbDefaults,
  SjbHardwareComponent,
} from "./types";
import type { QuoteStore } from "./state";
import { recalcItem } from "./pricing";
import { newId } from "./formatters";

export const SJB_SUPPLIER = "SJB Cabinets";

/**
 * Front edging.
 *
 * Drawn from the SAME 614 board decors as the carcass, not from the
 * platform's 11-entry EDGING_OPTIONS list (Steve, 2026-09-05: every decor SJB
 * stock has matching tape available). SJB's files carry no edging data of
 * their own, so the decor list is the only source there is — and it is a
 * superset of what is stocked as tape rather than a verified tape range.
 *
 * Keyed brand|colour|STRUCTURE, so 614 and not 524: PE and MG are different
 * physical finishes — Kronospan BLACK exists as both — and a retailer
 * matching tape to a mirror-gloss door wants the mirror-gloss tape, not
 * whichever of the two happened to sort first.
 *
 * No discount or uplift is shown against them. Edge tape is a flat cost
 * whatever the decor, so the carcass's "45% off" would be a claim about a
 * number that does not apply here.
 */
export const SJB_EDGING_MATCHING = "Matching carcass";

/** The stored edging value is the LABEL, not the colour key — it rides onto
 *  the line, the notes and the PDF as display text, exactly as the old
 *  EDGING_OPTIONS strings did, so nothing downstream had to change. */
export function edgingKeyForLabel(label: string, colours: SjbColour[]): string {
  if (!label || label === SJB_EDGING_MATCHING) return SJB_EDGING_MATCHING;
  return colours.find((c) => c.label === label)?.key ?? SJB_EDGING_MATCHING;
}

/** A colour as returned by GET /api/sjb-catalogue/colours. */
export interface SjbColour {
  key: string;
  brand: string;
  colour: string;
  structure: string;
  label: string;
  /** Raw board-group token, e.g. "45" or "PLUS 100". */
  group: string;
  kind: "discount" | "uplift" | "unreadable";
  /** Signed: 45 = 45% off the carcass, -100 = PLUS 100 uplift. */
  discPct: number;
  /** Sort key, cheapest carcass first: -45 … 0 … +150. Discounts are negative
   *  and uplifts positive, so a single ascending sort yields discounts
   *  high-to-low followed by uplifts low-to-high — which is also exactly the
   *  order the two GROUPS are rendered in. */
  priceRank: number;
  /** Pre-lowercased "brand colour structure code" for the filter box, so
   *  typing does not re-derive it for 614 rows on every keystroke. */
  search: string;
}

/** Which cabinets let the retailer pick a hinge side. Derived server-side from
 *  the door configuration ALONE — see modules/sjb_catalogue.js handingFor().
 *
 *  `source` is "catalogue" only for an API older than 2026-09-07, when column
 *  7 was still read as handing. It is Reduced Gable data (Steve's
 *  confirmation), so nothing derives a hinge side from a column any more. */
export interface SjbHandingChoice {
  choice: boolean;
  options: string[];
  source: "catalogue" | "derived" | "fixed_double" | "no_door";
}

/** A per-unit side choice that is NOT priced: Reduced Gable (column 7) and
 *  Blanking Panel Side (column 9).
 *
 *  Shaped, never a raw string. `offered: false` means "this unit takes none",
 *  and the whole field being ABSENT means the API is older than the feature —
 *  the picker must be able to tell those apart, because rendering the second
 *  as the first states something about the cabinet that nobody checked. */
export interface SjbSideChoice {
  offered: boolean;
  options: string[];
  source: "catalogue" | "category" | "not_offered";
}

/** Hinge drilling for one door leaf. Count AND positions come from the door's
 *  HEIGHT — see modules/sjb_catalogue.js drillingForDoor(). Positions are mm
 *  from the top of the door. */
export interface SjbDoorDrilling {
  height: number;
  holes: number;
  positions: number[];
  tier: "standard" | "mid" | "tall" | "unknown";
}

export interface SjbDoorLeaf {
  height: number;
  width: number;
  curved: boolean;
  drilling: SjbDoorDrilling;
}

/** A drawer front: a leaf below the door-height threshold. It carries no
 *  hinge drilling — the front is screwed to the drawer box — but it is still
 *  a frontal, with a size and a price, and it has to be ordered. */
export interface SjbFrontLeaf {
  height: number;
  width: number;
  curved: boolean;
}

/** Every leaf on the cabinet, in the order the recipe lists them, which is
 *  the order they sit on the unit — drawer front above door. The frontal
 *  picker builds one row per entry, so this array is the ordering contract. */
export interface SjbLeaf {
  index: number;
  height: number;
  width: number;
  curved: boolean;
  /** The PRICING and DRILLING partition: a door is side-hung and drilled, a
   *  front is not. Do not use this for wording — see `part`. */
  kind: "door" | "front";
  /** What the leaf physically IS, for labelling. `kind: "front"` covers three
   *  different parts and used to call all of them a drawer front:
   *    drawer-front  a front on a drawer box
   *    filler        a 110mm fixed infill strip above an oven aperture
   *                  (Steve confirmed 2026-09-07)
   *    top-hung      a short hinged flap, hung from its TOP edge, where the
   *                  recipe's `door_config` says `top` and it is the only
   *                  such flap on the unit — every bridging unit, plus the
   *                  microwave wall housing. 21 units. Drilled, but by its
   *                  own rule: see `drilling` below.
   *    lift-up       the same shape of leaf where the unit has more than one
   *                  (a bifold wall unit's pair, 8 units). Undrilled: the
   *                  file's hinge_qty 2 is for the PAIR and nobody has
   *                  specified how a bifold's holes divide between its
   *                  leaves, so no count is claimed for them.
   *  Absent on an API older than 2026-09-07, in which case the picker falls
   *  back to `kind` and says "Drawer front" as before. */
  part?: "door" | "drawer-front" | "filler" | "lift-up" | "top-hung";
  /** Hinge drilling for a TOP-HUNG flap, and present on nothing else.
   *
   *  `kind` stays "front" for these — it is the pricing and grouping
   *  partition, and a top-hung flap is still priced as a front — so the
   *  drilling had to ride on the leaf rather than widen `kind`.
   *
   *  `positions` is EMPTY BY DESIGN, not missing: the bores run along the top
   *  edge and nobody has specified where along it they sit. `edge` carries
   *  the fact that matters, so a PO can say "top-hung, 2 holes" without
   *  anyone inventing millimetres. Absent on an API older than 2026-09-10. */
  drilling?: { height: number; holes: number; positions: number[]; edge: "top"; tier: string };
}

export interface SjbDrilling {
  doors: SjbDoorLeaf[];
  /** Drawer-front leaves, with dimensions. Present from 2026-09-05; absent
   *  from any API older than that, in which case the picker falls back to
   *  doors alone rather than inventing fronts it has no sizes for. */
  fronts?: SjbFrontLeaf[];
  leaves?: SjbLeaf[];
  /** The unit's top-hung flap, when it has one — a shortcut so a caller need
   *  not re-derive it from `leaves`. `null` means "this unit has none";
   *  absent means an API older than 2026-09-10 that does not know the
   *  concept, which is a different thing and must not read as "none". */
  topHung?: { leafIndex: number; holes: number; edge: "top" } | null;
  /** Count of leaves excluded as drawer fronts (below the door height
   *  threshold). Still a count; `fronts.length` is the same number. */
  drawerFronts: number;
  /** Total holes the door heights call for. */
  specHoles: number;
  /** How the leaf count was arrived at when a leading multiplier made the
   *  cell ambiguous — "2 x 175 x 496 + 355 x 496" is a 3-drawer pack while
   *  the identically-shaped "2 x 140 x 396 + 570 x 396" is 4 leaves, so the
   *  priced columns arbitrate. Diagnostic only; absent on an older API. */
  leafReading?: "global" | "perChunk" | "unambiguous";
  leafDecidedBy?: "boxes" | "hinges" | "default" | null;
  recipeHingeQty: number;
  hinged: boolean;
  /** null = cannot attribute (row mixes doors and drawer fronts, or is
   *  unhinged). false = the recipe's hinge count disagrees with what the door
   *  heights require — surfaced, never silently corrected, because the hinge
   *  count is priced. */
  hingeQtyMatchesSpec: boolean | null;
}

/** A door catalogue SJB have verified as usable for frontals. Server config,
 *  not code — see /api/sjb-catalogue/frontal-suppliers. */
export interface SjbFrontalSupplier {
  slug: string;
  label: string;
}

export interface SjbCabinetSummary {
  code: string;
  desc: string;
  dims: string;
  categories?: string[];
  notes?: string;
  doorConfig: string;
  /** Raw column values, for the search row only. The picker uses the SHAPED
   *  choices on SjbPricedLine instead. */
  reducedGable?: string;
  blankingPanel?: string;
  tradePrice?: number;
  discountLabel?: string;
}

interface SjbPricedCarcass {
  code: string;
  desc: string;
  listPrice: number;
  discPct: number;
  price: number;
  discountLabel: string;
}

export interface SjbPricedLine {
  ok: boolean;
  sjbCode: string;
  desc: string;
  dims: string;
  /** Browsing categories from the recipe. Pipe-delimited in the source and
   *  split server-side, so a unit can sit under more than one pill. Absent
   *  from an API older than 2026-09-05. */
  categories?: string[];
  /** Free-text manufacturing / site note per SJB Code. Pre-fills the
   *  picker's Notes field; the retailer edits from there. Not priced. */
  notes?: string;
  carcass: SjbPricedCarcass;
  board: { key: string; brand: string; colour: string; structure: string; group: string; kind: string } | null;
  hingeFinish: "standard" | "onyx";
  drawerSystem: "antaro" | "legrabox";
  handingChoice: SjbHandingChoice;
  /** Column 7. 83 SKUs (Gola Larder / Gola Oven Housing / Gola FF) offer
   *  Left|Right|Both. Optional: absent on an API older than 2026-09-07. */
  reducedGable?: SjbSideChoice;
  /** Column 9. 104 corner-shaped SKUs offer Left|Right — which side the
   *  blanked return sits on. Optional for the same reason. */
  blankingPanel?: SjbSideChoice;
  drilling: SjbDrilling;
  doorConfig: string;
  hardware: SjbHardwareComponent[];
  hardwareTotal: number;
  tradePrice: number;
  blocked: Array<{ code: string | null; reason: string; detail?: string }>;
}

// ── Quote-level defaults ───────────────────────────────────────────────────

const FALLBACK: SjbDefaults = {
  boardKey: "", boardLabel: "", hingeFinish: "standard", drawerSystem: "antaro",
  edgingColour: "Matching carcass",
  // Doors on. Matches every quote built before the toggle existed, and a
  // cabinet that silently arrives with no front is worse than one the
  // retailer has to switch off. See SjbDefaults.frontalOn.
  frontalOn: true,
  // Empty, not a guess at "tkc". Which catalogues are enabled is server
  // config, and defaulting to one that a tenant has not enabled would show a
  // selection the retailer cannot act on. The picker still preselects when
  // exactly ONE catalogue comes back, which is the common case.
  frontalSlug: "",
  frontalRange: "",
  frontalColour: "",
  frontalTier: "",
  // On by default: a kitchen is one door range, so re-picking it per cabinet
  // is the unusual case, not the normal one.
  rememberFrontal: true,
};

export function getSjbDefaults(store: QuoteStore): SjbDefaults {
  return { ...FALLBACK, ...(store.get().defaults.sjb ?? {}) };
}

/**
 * Update one or more quote-level defaults.
 *
 * Touches `defaults.sjb` and nothing else. Existing lines keep the colour,
 * hinge finish and drawer system they were added with — see the module
 * header. If you ever need "apply to all lines", add it as a separate,
 * explicitly-named action with its own confirm; do not widen this one.
 */
export function setSjbDefaults(store: QuoteStore, patch: Partial<SjbDefaults>): void {
  store.set((draft) => {
    draft.defaults.sjb = { ...FALLBACK, ...(draft.defaults.sjb ?? {}), ...patch };
  });
}

// ── API ────────────────────────────────────────────────────────────────────

let colourCache: SjbColour[] | null = null;

export async function loadSjbColours(): Promise<SjbColour[]> {
  if (colourCache) return colourCache;
  const data = await api<{ results?: SjbColour[] }>("/sjb-catalogue/colours");
  colourCache = data.results ?? [];
  return colourCache;
}

/** A browsing pill. `count` comes from the server so a pill that would open
 *  on an empty list is never offered. */
export interface SjbCategory { name: string; count: number; }

let categoryCache: SjbCategory[] | null = null;

/** Categories are derived from the recipe server-side, not hard-coded — a
 *  category that leaves the file stops being offered without a client change.
 *  A 404 means an API older than 2026-09-05: return none and let the caller
 *  render no pills rather than an error, since browsing still works. */
export async function loadSjbCategories(): Promise<SjbCategory[]> {
  if (categoryCache) return categoryCache;
  try {
    const data = await api<{ results?: SjbCategory[] }>("/sjb-catalogue/categories");
    categoryCache = data.results ?? [];
  } catch { categoryCache = []; }
  return categoryCache;
}

let frontalSupplierCache: SjbFrontalSupplier[] | null = null;

export async function loadSjbFrontalSuppliers(): Promise<SjbFrontalSupplier[]> {
  if (frontalSupplierCache) return frontalSupplierCache;
  const data = await api<{ suppliers?: SjbFrontalSupplier[] }>("/sjb-catalogue/frontal-suppliers");
  frontalSupplierCache = data.suppliers ?? [];
  return frontalSupplierCache;
}

/** For tests / debugging — clears the in-memory caches so a harness can
 *  exercise a failed load after a successful one. Mirrors
 *  drilling-charge.ts:_resetDrillingChargeCacheForTests. */
export function _resetSjbCachesForTests(): void {
  colourCache = null;
  frontalSupplierCache = null;
  categoryCache = null;
}

export async function searchSjbCabinets(q: string, category?: string): Promise<SjbCabinetSummary[]> {
  const qs = new URLSearchParams({ q });
  if (category) qs.set("category", category);
  const data = await api<{ results?: SjbCabinetSummary[] }>(
    `/sjb-catalogue/cabinets?${qs.toString()}`,
  );
  return data.results ?? [];
}

export async function priceSjbLine(
  sjbCode: string,
  opts: { boardKey: string; hingeFinish: string; drawerSystem: string },
): Promise<SjbPricedLine> {
  const qs = new URLSearchParams({
    colour: opts.boardKey, hinge: opts.hingeFinish, drawer: opts.drawerSystem,
  });
  return api<SjbPricedLine>(`/sjb-catalogue/price/${encodeURIComponent(sjbCode)}?${qs}`);
}

// ── Item construction ──────────────────────────────────────────────────────

/**
 * Project a priced API response onto a QuoteItem.
 *
 * `listPrice`/`discPct` describe the CARCASS. The hardware total rides in
 * `sjbHardwareTotal` so the board adjustment cannot reach it, and
 * pricing.ts:recalcItem sums the two into `tradePrice`.
 */
export function sjbLineToItem(
  priced: SjbPricedLine,
  opts: { qty: number; zoneId?: string; markupPct: number; boardLabel: string },
): QuoteItem {
  const item: QuoteItem = {
    id: newId("i"),
    category: "cabinets",
    supplier: SJB_SUPPLIER,
    code: priced.sjbCode,
    desc: priced.desc,
    qty: opts.qty || 1,
    width: 0,
    height: 0,
    depth: 0,
    notes: buildNotes(priced, opts.boardLabel),
    listPrice: priced.carcass.listPrice,
    discPct: priced.carcass.discPct,
    tradePrice: 0,
    markupMode: "pct",
    markupPct: opts.markupPct,
    markupGbp: 0,
    retailPrice: 0,
    // Line-owned copies of the three choices. Seeded from the quote default
    // at add time; from here on the line is the source of truth.
    sjbCode: priced.sjbCode,
    sjbBoardKey: priced.board?.key ?? "",
    sjbBoardLabel: opts.boardLabel,
    sjbHingeFinish: priced.hingeFinish,
    sjbDrawerSystem: priced.drawerSystem,
    sjbHardwareTotal: priced.hardwareTotal,
    sjbHardware: priced.hardware,
    sjbDiscountLabel: priced.carcass.discountLabel,
  };
  const [w, , d] = parseDims(priced.dims);
  if (w) item.width = w;
  if (d) item.depth = d;
  const h = parseDims(priced.dims)[1];
  if (h) item.height = h;
  // Derives tradePrice = carcass-after-board + hardware, then retailPrice.
  return recalcItem(item, "listPrice");
}

/** Recipe dims read "720 x 600 x 570" = height x width x depth. */
function parseDims(dims: string): [number, number, number] {
  const m = String(dims || "").match(/(\d+)\s*x\s*(\d+)\s*x\s*(\d+)/i);
  if (!m) return [0, 0, 0];
  return [Number(m[2]), Number(m[1]), Number(m[3])];
}

function buildNotes(priced: SjbPricedLine, boardLabel: string): string {
  const parts: string[] = [];
  if (boardLabel) parts.push(`Board: ${boardLabel}`);
  if (priced.carcass.discountLabel) parts.push(priced.carcass.discountLabel);
  parts.push(`Hinges: ${priced.hingeFinish === "onyx" ? "Onyx" : "Standard"}`);
  if (priced.hardware.some((h) => h.kind === "drawer")) {
    parts.push(`Drawers: ${priced.drawerSystem === "legrabox" ? "Legrabox" : "Antaro"}`);
  }
  return parts.join(" | ");
}

// ── Per-line override ──────────────────────────────────────────────────────

/**
 * Re-price ONE line after the retailer changes its colour, hinge finish or
 * drawer system. Returns a new item; the caller writes it back to the store.
 * No other line is read or touched.
 */
export async function repriceSjbItem(
  item: QuoteItem,
  patch: { boardKey?: string; boardLabel?: string; hingeFinish?: "standard" | "onyx"; drawerSystem?: "antaro" | "legrabox" },
): Promise<QuoteItem> {
  const boardKey = patch.boardKey ?? item.sjbBoardKey ?? "";
  const boardLabel = patch.boardLabel ?? item.sjbBoardLabel ?? "";
  const hingeFinish = patch.hingeFinish ?? item.sjbHingeFinish ?? "standard";
  const drawerSystem = patch.drawerSystem ?? item.sjbDrawerSystem ?? "antaro";

  const priced = await priceSjbLine(item.sjbCode ?? item.code, { boardKey, hingeFinish, drawerSystem });

  const next: QuoteItem = {
    ...item,
    listPrice: priced.carcass.listPrice,
    discPct: priced.carcass.discPct,
    sjbBoardKey: priced.board?.key ?? "",
    sjbBoardLabel: boardLabel,
    sjbHingeFinish: priced.hingeFinish,
    sjbDrawerSystem: priced.drawerSystem,
    sjbHardwareTotal: priced.hardwareTotal,
    sjbHardware: priced.hardware,
    sjbDiscountLabel: priced.carcass.discountLabel,
    notes: buildNotes(priced, boardLabel),
  };
  return recalcItem(next, "listPrice");
}

// ── ABS panels (2026-09-16) ────────────────────────────────────────────────
//
// Panels are standalone edged-all-round products, not cabinet parts: the
// retailer asks for a size and the server returns the cheapest stocked panel
// covering it, at each of the three thicknesses.

export interface SjbPanelOption {
  thickness: number;
  /** Absent when nothing at this thickness covers the request. */
  code?: string;
  desc?: string;
  height?: number;
  width?: number;
  /** What this panel COSTS in the chosen colour — the number to show. Equal
   *  to `listPrice` when no colour has been chosen. */
  price?: number;
  /** DTN's undiscounted file price. Optional only because an API older than
   *  2026-09-16 does not send it; `price` is the fallback, and on that API
   *  the two were the same thing. */
  listPrice?: number;
  /** Signed board-group adjustment: 45 = 45% off, -100 = PLUS 100 uplift.
   *  Rides onto the line so the discount stays visible and removable rather
   *  than being baked into one number. */
  discPct?: number;
  /** "45% board discount" / "+20% board uplift". Never a bare "% off". */
  discountLabel?: string;
  /** The panel only fits turned on its side — grain direction is then the
   *  retailer's call, so this is surfaced rather than applied silently. */
  rotated?: boolean;
  trimHeight?: number;
  trimWidth?: number;
  exact?: boolean;
  unavailable?: boolean;
  /** The SMALLEST covering panel, when that is not the cheapest one. Shown to
   *  explain why a larger panel is being quoted; never used as the price. */
  nearest?: {
    code: string; desc: string; height: number; width: number;
    price: number; saving: number;
  } | null;
}

export interface SjbPanelQuote {
  ok: boolean;
  error?: string;
  requested: { height: number; width: number };
  /** The colour these prices were calculated in, echoed back by the server.
   *  `null` means none was asked for and every price is full list — which the
   *  picker says out loud rather than leaving the retailer to infer. */
  board?: {
    key: string; brand: string; colour: string; structure: string;
    group: string; kind: string; discPct: number; discountLabel: string;
  } | null;
  options: SjbPanelOption[];
  /** Always false — cut/edge/drill are separate addable lines, never folded
   *  into a panel price. Read rather than assumed so a client cannot drift. */
  processingIncluded: boolean;
}

export interface SjbPanelMeta {
  ok: boolean;
  total: number;
  thicknesses: number[];
  maxAnyDimension: number;
  byThickness: Record<string, { count: number; maxHeight?: number; maxWidth?: number;
                                minHeight?: number; minWidth?: number }>;
}

let PANEL_META: SjbPanelMeta | null = null;

export async function loadSjbPanelMeta(): Promise<SjbPanelMeta> {
  if (PANEL_META) return PANEL_META;
  const data = await api<SjbPanelMeta>("/sjb-catalogue/panels/meta");
  PANEL_META = data;
  return data;
}

/** @param boardKey BRAND|COLOUR|STRUCTURE. Omitted = full list, which is the
 *  honest answer to "price this with no colour chosen" rather than a colour
 *  picked on the retailer's behalf. */
export async function priceSjbPanel(
  heightMm: number, widthMm: number, boardKey = "",
): Promise<SjbPanelQuote> {
  const qs = new URLSearchParams({ h: String(heightMm), w: String(widthMm) });
  if (boardKey) qs.set("colour", boardKey);
  return api<SjbPanelQuote>(`/sjb-catalogue/panels/price?${qs}`);
}

/**
 * A chosen panel, as a quote line.
 *
 * THE BOARD GROUP APPLIES (Steve, 2026-09-16). This function used to say the
 * opposite — "a panel is bought as a finished product, not built from a
 * carcass, so there is no board group in play" — and that was wrong: a panel
 * IS board, cut from the same sheet in the same decor. The effect of the
 * mistake was that every panel quoted at full list whatever colour the
 * retailer had chosen. DTN's own CEGROUP rows settle it; see the block
 * comment above pricePanel() in modules/sjb_catalogue.js.
 *
 * The line carries {listPrice, discPct} and NOT a pre-discounted number, for
 * the same reason the carcass does: the adjustment has to stay visible on the
 * line, editable, and re-derivable by pricing.ts:calcTrade. A line holding
 * only the net price would look hand-priced and could never be explained.
 *
 * `listPrice`/`discPct` are read defensively: an API older than 2026-09-16
 * sends neither, and on that API `price` WAS the list price, so falling back
 * to it changes nothing there and cannot double-apply anything here.
 */
export function sjbPanelToItem(
  opt: SjbPanelOption,
  quote: SjbPanelQuote,
  o: {
    qty: number; zoneId?: string; markupPct: number;
    boardKey?: string; boardLabel?: string; edgingColour?: string;
  },
): QuoteItem {
  const req = `${quote.requested.height} x ${quote.requested.width}mm`;
  const trim = (opt.trimHeight || 0) + (opt.trimWidth || 0) > 0
    ? ` · cut down from ${opt.height} x ${opt.width}mm`
    : "";
  const edging = o.edgingColour && o.edgingColour !== SJB_EDGING_MATCHING ? o.edgingColour : "";
  const notes = [
    `Requested ${req}`,
    opt.exact ? "stock size, no cutting" : `stock panel ${opt.height} x ${opt.width}mm`,
    opt.rotated ? "PANEL TURNED — check grain direction on a woodgrain decor" : "",
    // The decor is on the line because the panel is made from it: two panels
    // at the same size and thickness in different colours are different
    // products at different prices, and the notes are what a fabricator gets.
    o.boardLabel ? `Board: ${o.boardLabel}` : "",
    // "Matching carcass" is the default and says nothing, so it is omitted —
    // a note that is on every line is read by nobody.
    edging ? `Edging: ${edging}` : "",
    // Said on the line itself, because the PO export carries description and
    // notes but nothing else, and a fabricator reading only this needs to
    // know the cutting was quoted separately or not at all.
    "cutting / edging / drilling charged separately",
  ].filter(Boolean).join(" · ");

  const item: QuoteItem = {
    id: newId("i"),
    category: "panels",
    supplier: SJB_SUPPLIER,
    code: opt.code || "",
    desc: `${opt.thickness}mm panel ${req}${trim}`,
    qty: o.qty || 1,
    width: quote.requested.width,
    height: quote.requested.height,
    depth: opt.thickness,
    notes,
    listPrice: opt.listPrice ?? opt.price ?? 0,
    discPct: opt.discPct ?? 0,
    tradePrice: 0,
    markupMode: "pct",
    markupPct: o.markupPct,
    markupGbp: 0,
    retailPrice: 0,
    // Snapshot of the colour on THIS line, exactly as an SJB cabinet line
    // carries it: changing the quote default afterwards must not reprice a
    // panel that has already been added.
    sjbBoardKey: o.boardKey || "",
    sjbBoardLabel: o.boardLabel || "",
    sjbEdgingColour: o.edgingColour || "",
    // The field the quote row, the pencil editor and the customer spec read —
    // set exactly as the SJB cabinet picker sets it. Without it a panel's
    // edging lived only in sjbEdgingColour and the notes, and showed nowhere.
    edging: o.edgingColour || "",
    sjbDiscountLabel: opt.discountLabel || "",
  };
  return recalcItem(item, "listPrice");
}

// ── DTN cut / drill processing charges ─────────────────────────────────────

export interface SjbProcessingCharge {
  code: string;
  desc: string;
  /** panel | hole | sheet | each — what a quantity of 1 buys. */
  unit: string;
  price: number;
  /** Sheet charges already carry the board discount; never re-discount them. */
  alreadyNet: boolean;
}

let PROCESSING_CACHE: SjbProcessingCharge[] | null = null;

export async function loadSjbProcessing(): Promise<SjbProcessingCharge[]> {
  if (PROCESSING_CACHE) return PROCESSING_CACHE;
  const data = await api<{ results?: SjbProcessingCharge[] }>("/sjb-catalogue/processing");
  PROCESSING_CACHE = data.results ?? [];
  return PROCESSING_CACHE;
}

/** Human wording for a unit, used on the row and in the line description. */
export function processingUnitLabel(unit: string): string {
  switch (unit) {
    case "panel": return "per panel";
    case "hole":  return "per hole";
    case "sheet": return "per sheet";
    default:      return "each";
  }
}

/** A processing charge as an ordinary quote line. Deliberately NOT attached to
 *  a panel line: which processing a job needs is a separate decision, and a
 *  bundled charge cannot be removed without editing a price. */
export function sjbProcessingToItem(
  charge: SjbProcessingCharge,
  o: { qty: number; markupPct: number },
): QuoteItem {
  const item: QuoteItem = {
    id: newId("i"),
    category: "sundries",
    supplier: SJB_SUPPLIER,
    code: charge.code,
    desc: charge.desc,
    qty: o.qty || 1,
    width: 0, height: 0, depth: 0,
    notes: `DTN processing charge · ${processingUnitLabel(charge.unit)}`,
    listPrice: charge.price,
    discPct: 0,
    tradePrice: 0,
    markupMode: "pct",
    markupPct: o.markupPct,
    markupGbp: 0,
    retailPrice: 0,
  };
  return recalcItem(item, "listPrice");
}

/** Test seam — mirrors _resetSjbCachesForTests for the two new caches. */
export function _resetSjbPanelCachesForTests(): void {
  PANEL_META = null;
  PROCESSING_CACHE = null;
}
